// src/pages/supplier/ForecastDashboard.jsx
import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supplierForecastApi } from '../../api/services'
import {
  Card, CardHeader, CardBody, Badge, PageSpinner, Button, Empty,
} from '../../components/common/ui'
import {
  TrendingUp, Calendar, Package, Users, Zap, ArrowRight,
  ChevronDown, ChevronUp, Sun, Snowflake, Flower, CloudRain,
  RefreshCw, BarChart2, AlertCircle, Target,
} from 'lucide-react'
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip,
  ResponsiveContainer, CartesianGrid, Legend,
} from 'recharts'

const fmt = v => `₹${Number(v || 0).toLocaleString('en-IN')}`
const fmtK = v => {
  const n = Number(v || 0)
  if (n >= 100000) return `₹${(n / 100000).toFixed(1)}L`
  if (n >= 1000) return `₹${(n / 1000).toFixed(1)}K`
  return fmt(v)
}

const SEASON_ICONS = {
  festive: { icon: Flower, color: 'text-orange-500', bg: 'bg-orange-50' },
  wedding: { icon: Target, color: 'text-pink',       bg: 'bg-pink-50' },
  summer:  { icon: Sun,    color: 'text-yellow-500', bg: 'bg-yellow-50' },
  spring:  { icon: Flower, color: 'text-green-500',  bg: 'bg-green-50' },
  monsoon: { icon: CloudRain, color: 'text-blue-500', bg: 'bg-blue-50' },
}

function SeasonalNotes({ notes }) {
  if (!notes?.length) return null
  return (
    <div className="space-y-2">
      {notes.map((note, i) => {
        const style = SEASON_ICONS[note.type] || SEASON_ICONS.spring
        const Icon = style.icon
        return (
          <div key={i} className={`flex items-start gap-3 p-3 rounded-xl ${style.bg}`}>
            <Icon size={16} className={`${style.color} mt-0.5 flex-shrink-0`} />
            <p className="text-sm text-gray-700">{note.text}</p>
          </div>
        )
      })}
    </div>
  )
}

function CategoryForecastCard({ cat, onViewDetail }) {
  const [expanded, setExpanded] = useState(false)
  const months = cat.summary?.months || []

  return (
    <Card>
      <div
        className="px-5 py-4 flex items-center justify-between cursor-pointer hover:bg-surface/50 transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <p className="font-bold text-sm">{cat.category_name}</p>
            <Badge variant={cat.has_forecast ? 'green' : 'grey'}>
              {cat.has_forecast ? 'Forecast Ready' : 'No Data'}
            </Badge>
          </div>
          <p className="text-xs text-muted mt-0.5">{cat.product_count} active product(s)</p>
        </div>

        {cat.has_forecast && cat.summary && (
          <div className="flex items-center gap-6 mr-4">
            <div className="text-right">
              <p className="text-[10px] text-muted uppercase">Predicted Units</p>
              <p className="text-sm font-bold">{cat.summary.total_predicted_units}</p>
            </div>
            <div className="text-right">
              <p className="text-[10px] text-muted uppercase">Predicted GMV</p>
              <p className="text-sm font-bold text-pink">{fmtK(cat.summary.total_predicted_gmv)}</p>
            </div>
          </div>
        )}

        {expanded ? <ChevronUp size={16} className="text-muted" /> : <ChevronDown size={16} className="text-muted" />}
      </div>

      {expanded && cat.has_forecast && months.length > 0 && (
        <CardBody className="pt-0 space-y-4">
          {/* Monthly bar chart */}
          <ResponsiveContainer width="100%" height={180}>
            <BarChart data={months}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="month" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 10 }} width={40} />
              <Tooltip formatter={(v, name) => [name === 'gmv' ? fmtK(v) : v, name === 'gmv' ? 'GMV' : 'Units']} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="units" fill="#FF3D7F" radius={[4, 4, 0, 0]} name="Units" />
            </BarChart>
          </ResponsiveContainer>

          {/* Confidence bands table */}
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-muted uppercase">
                  <th className="text-left pb-2 font-semibold">Month</th>
                  <th className="text-right pb-2 font-semibold">Predicted</th>
                  <th className="text-right pb-2 font-semibold">Low Est.</th>
                  <th className="text-right pb-2 font-semibold">High Est.</th>
                  <th className="text-right pb-2 font-semibold">GMV</th>
                </tr>
              </thead>
              <tbody>
                {months.map((m, i) => (
                  <tr key={i} className="border-t border-border">
                    <td className="py-2 font-bold">{m.month}</td>
                    <td className="py-2 text-right">{m.units} units</td>
                    <td className="py-2 text-right text-muted">{m.conf_lower}</td>
                    <td className="py-2 text-right text-muted">{m.conf_upper}</td>
                    <td className="py-2 text-right font-bold text-pink">{fmtK(m.gmv)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <button
            onClick={() => onViewDetail(cat.category_id)}
            className="flex items-center gap-1 text-xs text-pink font-bold hover:underline"
          >
            View 90-day daily forecast <ArrowRight size={12} />
          </button>
        </CardBody>
      )}

      {expanded && !cat.has_forecast && (
        <CardBody className="pt-0">
          <p className="text-sm text-muted">Insufficient order history. At least 4 months of orders are needed to generate forecasts.</p>
        </CardBody>
      )}
    </Card>
  )
}

function ReorderTable({ reorders }) {
  if (!reorders?.length) {
    return (
      <Card>
        <CardHeader><span className="font-bold text-sm">Predicted Reorders</span></CardHeader>
        <CardBody>
          <Empty icon={<Calendar size={32} className="text-muted" />} title="No upcoming reorders" description="Reorder predictions appear when buyers have repeat purchase patterns." />
        </CardBody>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader>
        <span className="font-bold text-sm">Predicted Reorders (Next 30 Days)</span>
        <Badge variant="blue">{reorders.length} upcoming</Badge>
      </CardHeader>
      <div className="divide-y divide-border">
        {reorders.map(r => (
          <div key={r.id} className="px-5 py-3 flex items-center justify-between gap-3">
            <div className="flex-1 min-w-0">
              <p className="font-bold text-sm truncate">{r.product}</p>
              <p className="text-[10px] text-muted">{r.sku} &middot; Buyer: {r.buyer}</p>
            </div>
            <div className="text-center flex-shrink-0">
              <p className="text-xs font-bold">
                {r.days_until <= 0 ? (
                  <span className="text-red-500">Overdue</span>
                ) : r.days_until <= 3 ? (
                  <span className="text-orange-500">{r.days_until}d</span>
                ) : (
                  <span>{r.days_until}d</span>
                )}
              </p>
              <p className="text-[10px] text-muted">{r.predicted_date}</p>
            </div>
            <div className="text-right flex-shrink-0 w-20">
              <p className="text-xs font-bold">{r.predicted_qty} units</p>
              <p className="text-[10px] text-muted">{fmt(r.avg_order_value)} avg</p>
            </div>
            <div className="flex-shrink-0 w-16 text-right">
              <div className="inline-flex items-center gap-1">
                <div className="w-10 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full ${r.confidence >= 80 ? 'bg-green-400' : r.confidence >= 60 ? 'bg-yellow-400' : 'bg-orange-400'}`}
                    style={{ width: `${r.confidence}%` }}
                  />
                </div>
                <span className="text-[10px] font-bold text-muted">{r.confidence}%</span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </Card>
  )
}

function DailyForecastModal({ categoryId, onClose }) {
  const { data, isLoading } = useQuery({
    queryKey: ['forecast-detail', categoryId],
    queryFn: () => supplierForecastApi.category(categoryId),
    enabled: !!categoryId,
  })

  if (!categoryId) return null

  const forecast = data?.data?.data || {}
  const daily = forecast.daily_forecast || []
  const summary = forecast.summary || {}
  const notes = forecast.seasonal_notes || []

  // Aggregate to weekly for chart readability
  const weekly = []
  for (let i = 0; i < daily.length; i += 7) {
    const week = daily.slice(i, i + 7)
    weekly.push({
      label: `W${Math.floor(i / 7) + 1}`,
      date_range: `${week[0]?.target_date?.slice(5)} – ${week[week.length - 1]?.target_date?.slice(5)}`,
      units: Math.round(week.reduce((s, d) => s + d.predicted_units, 0)),
      gmv: Math.round(week.reduce((s, d) => s + d.predicted_gmv, 0)),
      lower: Math.round(week.reduce((s, d) => s + d.confidence_lower, 0)),
      upper: Math.round(week.reduce((s, d) => s + d.confidence_upper, 0)),
    })
  }

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-start justify-center pt-10 px-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl max-w-3xl w-full max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-border flex items-center justify-between sticky top-0 bg-white rounded-t-2xl z-10">
          <div>
            <h2 className="font-display font-bold text-lg">{forecast.category_name || 'Category'} — 90-Day Forecast</h2>
            <p className="text-xs text-muted">{forecast.history_months || 0} months of history &middot; Model: {forecast.model || 'v1'}</p>
          </div>
          <button onClick={onClose} className="text-muted hover:text-gray-900 text-lg font-bold px-2">x</button>
        </div>

        <div className="p-6 space-y-6">
          {isLoading && <PageSpinner />}

          {!isLoading && forecast.error && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 text-sm text-yellow-800">
              <AlertCircle size={14} className="inline mr-1" /> {forecast.error}
            </div>
          )}

          {!isLoading && !forecast.error && (
            <>
              {/* Summary KPIs */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div className="bg-surface rounded-xl p-3">
                  <p className="text-[10px] text-muted uppercase">30-Day Units</p>
                  <p className="text-lg font-bold">{summary.next_30_days_units}</p>
                </div>
                <div className="bg-surface rounded-xl p-3">
                  <p className="text-[10px] text-muted uppercase">30-Day GMV</p>
                  <p className="text-lg font-bold text-pink">{fmtK(summary.next_30_days_gmv)}</p>
                </div>
                <div className="bg-surface rounded-xl p-3">
                  <p className="text-[10px] text-muted uppercase">90-Day Units</p>
                  <p className="text-lg font-bold">{summary.next_90_days_units}</p>
                </div>
                <div className="bg-surface rounded-xl p-3">
                  <p className="text-[10px] text-muted uppercase">Peak Month</p>
                  <p className="text-lg font-bold text-orange-500">{summary.peak_month}</p>
                </div>
              </div>

              {/* Weekly forecast chart with confidence bands */}
              <div>
                <p className="text-sm font-bold mb-3">Weekly Predicted Units (with confidence range)</p>
                <ResponsiveContainer width="100%" height={250}>
                  <AreaChart data={weekly}>
                    <defs>
                      <linearGradient id="confGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#FF3D7F" stopOpacity={0.15} />
                        <stop offset="100%" stopColor="#FF3D7F" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="label" tick={{ fontSize: 10 }} />
                    <YAxis tick={{ fontSize: 10 }} width={40} />
                    <Tooltip
                      content={({ active, payload }) => {
                        if (!active || !payload?.length) return null
                        const d = payload[0].payload
                        return (
                          <div className="bg-white border border-border rounded-lg shadow-lg p-3 text-xs">
                            <p className="font-bold">{d.date_range}</p>
                            <p>Units: <strong>{d.units}</strong></p>
                            <p>GMV: <strong>{fmtK(d.gmv)}</strong></p>
                            <p className="text-muted">Range: {d.lower} – {d.upper}</p>
                          </div>
                        )
                      }}
                    />
                    <Area type="monotone" dataKey="upper" stroke="none" fill="url(#confGrad)" />
                    <Area type="monotone" dataKey="lower" stroke="none" fill="white" />
                    <Area type="monotone" dataKey="units" stroke="#FF3D7F" strokeWidth={2} fill="none" dot={{ r: 3, fill: '#FF3D7F' }} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>

              {/* Seasonal notes */}
              {notes.length > 0 && (
                <div>
                  <p className="text-sm font-bold mb-2">Seasonal Insights</p>
                  {notes.map((note, i) => (
                    <p key={i} className="text-xs text-muted mb-1">- {note}</p>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export default function ForecastDashboard() {
  const [detailCategoryId, setDetailCategoryId] = useState(null)
  const queryClient = useQueryClient()

  const { data, isLoading } = useQuery({
    queryKey: ['supplier-forecast'],
    queryFn: () => supplierForecastApi.index(),
  })

  const computeMut = useMutation({
    mutationFn: () => supplierForecastApi.compute(),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['supplier-forecast'] }),
  })

  if (isLoading) return <PageSpinner />

  const payload = data?.data?.data || {}
  const categories = payload.categories || []
  const reorders = payload.reorders || []
  const seasonal = payload.seasonal || []

  const withForecast = categories.filter(c => c.has_forecast)
  const totalUnits = withForecast.reduce((s, c) => s + (c.summary?.total_predicted_units || 0), 0)
  const totalGmv = withForecast.reduce((s, c) => s + (c.summary?.total_predicted_gmv || 0), 0)

  return (
    <div className="space-y-6 max-w-[1200px]">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-xl font-display font-bold">Demand Forecasting</h1>
          <p className="text-xs text-muted mt-0.5">90-day demand predictions by category with seasonal adjustments</p>
        </div>
        <Button
          variant="primary"
          size="sm"
          loading={computeMut.isPending}
          onClick={() => computeMut.mutate()}
        >
          <RefreshCw size={14} /> Recompute Forecasts
        </Button>
      </div>

      {computeMut.isSuccess && (
        <div className="bg-green-50 border border-green-200 rounded-xl p-3 text-sm text-green-800">
          {computeMut.data?.data?.data?.message || 'Forecasts recomputed.'}
        </div>
      )}

      {/* Summary KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Card className="p-4">
          <p className="text-xs text-muted font-semibold uppercase">Categories</p>
          <p className="text-2xl font-extrabold font-display">{categories.length}</p>
          <p className="text-[10px] text-muted">{withForecast.length} with forecasts</p>
        </Card>
        <Card className="p-4">
          <p className="text-xs text-muted font-semibold uppercase">Predicted Units</p>
          <p className="text-2xl font-extrabold font-display">{Math.round(totalUnits)}</p>
          <p className="text-[10px] text-muted">Next 90 days</p>
        </Card>
        <Card className="p-4">
          <p className="text-xs text-muted font-semibold uppercase">Predicted GMV</p>
          <p className="text-2xl font-extrabold font-display text-pink">{fmtK(totalGmv)}</p>
          <p className="text-[10px] text-muted">Next 90 days</p>
        </Card>
        <Card className="p-4">
          <p className="text-xs text-muted font-semibold uppercase">Upcoming Reorders</p>
          <p className="text-2xl font-extrabold font-display text-blue-600">{reorders.length}</p>
          <p className="text-[10px] text-muted">Next 30 days</p>
        </Card>
      </div>

      {/* Seasonal Notes */}
      <SeasonalNotes notes={seasonal} />

      {/* Category Forecasts */}
      <div>
        <h2 className="font-bold text-sm mb-3 flex items-center gap-2">
          <BarChart2 size={15} /> Category Forecasts
        </h2>
        {categories.length === 0 ? (
          <Card>
            <CardBody>
              <Empty
                icon={<TrendingUp size={32} className="text-muted" />}
                title="No categories yet"
                description="Add products to categories to start seeing demand forecasts."
              />
            </CardBody>
          </Card>
        ) : (
          <div className="space-y-3">
            {categories.map(cat => (
              <CategoryForecastCard
                key={cat.category_id}
                cat={cat}
                onViewDetail={setDetailCategoryId}
              />
            ))}
          </div>
        )}
      </div>

      {/* Reorder Predictions */}
      <ReorderTable reorders={reorders} />

      {/* How it works */}
      <Card>
        <CardHeader><span className="font-bold text-sm">How Demand Forecasting Works</span></CardHeader>
        <CardBody className="text-xs text-muted space-y-2">
          <p><strong className="text-gray-700">Historical Analysis:</strong> We analyse 12-18 months of your order data per category to identify trends and patterns.</p>
          <p><strong className="text-gray-700">Seasonal Adjustments:</strong> Indian textile seasons (Diwali, wedding season, summer lull) are factored in with empirical seasonal indices.</p>
          <p><strong className="text-gray-700">Confidence Intervals:</strong> Near-term forecasts (30 days) have tighter confidence bands than long-term (90 days).</p>
          <p><strong className="text-gray-700">Reorder Predictions:</strong> Based on buyer purchase patterns — frequency, quantity, and recency — to predict when repeat buyers will order again.</p>
        </CardBody>
      </Card>

      {/* Detail Modal */}
      {detailCategoryId && (
        <DailyForecastModal
          categoryId={detailCategoryId}
          onClose={() => setDetailCategoryId(null)}
        />
      )}
    </div>
  )
}
